import { z } from "zod";
import {
  AtSymbolIcon,
  ExclamationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Button } from "../buttons/button";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import FormItem from "../FormItem";
import { registerFormSchema } from "../../lib/formSchemas";
import { useAuth } from "../../auth/AuthProvider";
import { useNavigate } from "react-router-dom";

export default function RegisterForm() {
  const auth = useAuth();

  const form = useForm<z.infer<typeof registerFormSchema>>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      email: auth.triedEmail,
      firstName: "",
      lastName: "",
      newsletter: false,
    },
  });
  const { register, formState: { errors }, handleSubmit } = form;
  const [isPending, setIsPending] = useState(false);
  let navigate = useNavigate();

  async function onSubmit(values: z.infer<typeof registerFormSchema>) {
    setIsPending(true);
    const res = await auth.registerUser(values);
    if (res && res.user) {
      navigate("/app/dashboard", { state: { welcome: true } });
    } else if (res && res.error) {
      navigate("/user-error");
    }
    setIsPending(false);
  }

  const errorMessage = Object.keys(errors).length ? "There are some fields to need to fix." : ""

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-3"
      >
        <div className="flex-1 rounded-lg bg-gray-50 px-6 pb-4 pt-8">
          <div className="text-center px-8">
            <h2 className="mb-4 text-2xl font-extrabold leading-none tracking-tight text-[var(--color-action-primary)] md:text-3xl lg:text-4xl">Create an account</h2>
            <p className="mb-6 text-md font-normal text-gray-500">Just a few details and you'll be all set.</p>
          </div>
          <div className="w-full mt-4 px-8">
            <FormItem
              register={register}
              fieldName="Email"
              fieldId="email"
              required={true}
              disabled={isPending}
              containerClass="text-left"
              placeholder="username@email.com"
              icon={(<AtSymbolIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500 peer-focus:text-gray-900" />)}
              error={errors?.email}
            />
            <FormItem
              register={register}
              fieldName="First Name"
              fieldId="firstName"
              required={true}
              disabled={isPending}
              placeholder="First name"
              error={errors?.firstName}
              containerClass="mt-4 text-left"
              icon={(<UserIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500 peer-focus:text-gray-900" />)}
            />
            <FormItem
              register={register}
              fieldName="Last Name"
              fieldId="lastName"
              required={true}
              disabled={isPending}
              placeholder="Last name"
              error={errors?.lastName}
              containerClass="mt-4 text-left"
              icon={(<UserIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500 peer-focus:text-gray-900" />)}
            />
            <label className="mt-4 flex items-center gap-2 text-left text-sm text-gray-700">
              <input
                type="checkbox"
                {...register("newsletter")}
                disabled={isPending}
                className="h-4 w-4 rounded border-gray-300"
              />
              Do you want to subscribe to the General DWF Newsletter?
            </label>
          </div>
          <div
            className="flex h-8 items-end space-x-1 px-8"
            aria-live="polite"
            aria-atomic="true"
          >
            {errorMessage && (
              <>
                <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                <p className="text-sm text-red-500">{errorMessage}</p>
              </>
            )}
          </div>
          <div className="flex max-w-[320px] m-auto mt-8 justify-center p-2">
            <Button
              className="rounded-lg"
              buttonType="filled"
              type="submit"
              aria-disabled={isPending}
            >
              Create Account
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
